import React, { useState, useEffect } from 'react';
import { AlertCircle, TrendingDown, Calendar, Users, Euro, Zap } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UnsoldRoomsManager = () => {
  const [unsoldRooms, setUnsoldRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState({});

  useEffect(() => {
    fetchUnsoldRooms();
  }, []);

  const fetchUnsoldRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/matching/unsold-rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnsoldRooms(response.data.unsold_rooms || []);
    } catch (error) {
      console.error('Error fetching unsold rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const createQuickDeal = async (room, customDiscount) => {
    setCreating(prev => ({ ...prev, [room.room_type]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const discount = customDiscount || room.suggested_discount;
      
      // Simulate deal creation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success(` Deal creato con ${discount}% di sconto!`);
      fetchUnsoldRooms();
    } catch (error) {
      toast.error('Errore nella creazione del deal');
    } finally {
      setCreating(prev => ({ ...prev, [room.room_type]: false }));
    }
  };

  const getUrgencyBadge = (urgency, daysUntil) => {
    if (urgency === 'high' || daysUntil <= 7) {
      return <Badge className="bg-red-100 text-red-800 border-red-300">Urgente - {daysUntil} giorni</Badge>;
    } else if (urgency === 'medium' || daysUntil <= 14) {
      return <Badge className="bg-orange-100 text-orange-800 border-orange-300">Medio - {daysUntil} giorni</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Pianifica - {daysUntil} giorni</Badge>;
  };

  if (loading) {
    return (
      <Card className="p-8 text-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin mx-auto"></div>
      </Card>
    );
  }

  if (unsoldRooms.length === 0) {
    return (
      <Card className="p-8 text-center bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Zap className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">Ottimo lavoro!</h3>
        <p className="text-slate-600">Tutte le tue camere hanno una buona occupancy. Continua così!</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-900">Camere da Ottimizzare</h3>
          <p className="text-sm text-slate-600">Crea deal last-minute per massimizzare il revenue</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-600">Potenziale Revenue</p>
          <p className="text-2xl font-black text-emerald-600">€{unsoldRooms.reduce((sum, r) => sum + r.potential_revenue, 0)}</p>
        </div>
      </div>

      {/* Rooms */}
      {unsoldRooms.map((room, index) => (
        <Card key={index} className="overflow-hidden hover:shadow-xl transition-all">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 border-b border-orange-200">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-lg font-bold text-slate-900">{room.room_type}</h4>
                <p className="text-sm text-slate-600">{room.dates.length} notti disponibili</p>
              </div>
              {getUrgencyBadge(room.urgency, room.days_until)}
            </div>
          </div>

          <div className="p-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-black text-orange-600">{room.occupancy_rate}%</div>
                <div className="text-xs text-slate-600">Occupancy</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-blue-600">{room.available_units}</div>
                <div className="text-xs text-slate-600">Disponibili</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-emerald-600">{room.suggested_discount}%</div>
                <div className="text-xs text-slate-600">Sconto Sugg.</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-purple-600">€{room.potential_revenue}</div>
                <div className="text-xs text-slate-600">Revenue Pot.</div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">Date:</span>
                <span>{room.dates.join(', ')}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => createQuickDeal(room)}
                disabled={creating[room.room_type]}
                className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 h-12"
              >
                {creating[room.room_type] ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Creazione...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Crea Deal Rapido (-{room.suggested_discount}%)
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="px-6"
                onClick={() => toast.info('Personalizza sconto')}
              >
                Personalizza
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default UnsoldRoomsManager;